<script lang="ts" setup>
import { onBeforeMount, onMounted, computed, watch } from 'vue'
import { useHead } from '@vueuse/head'

import { useSourcepointStore } from '@toni/rendering/src/stores/sourcepoint'
import { useConfigStore } from '@toni/rendering/src/stores/config'
import { useSessionStore } from '@toni/rendering/src/stores/session'

const adaId = useConfigStore().rsConfig.consent.vendor.ada as string
const sessionStore = useSessionStore()
const sourcepointStore = useSourcepointStore()
const simulateContentPassOrContentPass = computed(() => sessionStore.simulateContentPassOrContentPass)
const consentedVendors = computed(() => sourcepointStore.consentedVendors)
const adaHasConsent = computed(() => {
  return consentedVendors.value.includes(adaId)
})
const shouldLoadAda = computed(() => {
  return !simulateContentPassOrContentPass.value && adaHasConsent.value
})

const articleMapping = computed(() => ({
  'ems-native-adlabel': '[data-ems="ems-native-adlabel"]', // header ad label
  'ems-native-subheadline': '[data-ems="ems-native-subheadline"]', // article landmark (optional) roofline
  'ems-native-headline': '[data-ems="ems-native-headline"]', // article title
  'ems-native-headlinetext': '[data-ems="ems-native-headlinetext"]', // article introduction text
  'ems-native-mainimage': '[data-ems="ems-native-mainimage"]', // lead image
  'ems-native-imagetext': '[data-ems="ems-native-imagetext"]', // lead image headline
  'ems-native-copyright': '[data-ems="ems-native-copyright"]', // lead image copyright label
  'ems-native-articletext': '[data-ems="ems-native-articletext"] p' // article text
}))

const sharedMapping = computed(() => ({
  'ems-native-overallheadline': '[data-ems="ems-js-ada-native-overallheadline"] h2', // special headline
  'ems-native-backlink': '[data-ems="ems-js-ada-native-overallheadline"] a', // link of special headline
  'ems-native-teaserwrapper': '[data-ems="ems-native-teaserwrapper"]', // bottom teaserblock image (only one possible)
  'ems-native-teasercontainer': '[data-ems="ems-native-teasercontainer"]', // container of one teaser in the teaserblock
  'ems-native-teaserheadline': '[data-ems="ems-native-teaserheadline"]', // teaser headline
  'ems-native-teasersubheadline': '[data-ems="ems-native-teasersubheadline"]', // teaser landmark (optional) roofline
  'ems-native-teaseradlabel': '[data-ems="ems-native-teaseradlabel"]', // teaser ad label
  'ems-native-teaserimage': '[data-ems="ems-native-teaserimage"]', // teaser image
  'ems-native-teaserlink': '[data-ems="ems-native-teaserlink"]' // teaser link, links to article
}))

const injectAdaAutoNativeScript = () => {
  useHead({
    script: [
      {
        'data-vmid': 'ada-autonative-iframe-height',
        key: 'ada-autonative-iframe-height',
        src: 'https://static.emsservice.de/responsive/setIframeHeightParent.min.js',
        async: true
      }
    ]
  })
}

// only show roofline elements if they have content
const handleRooflineElements = () => {
  document.querySelectorAll('div[data-ems="ems-native-teasersubheadline"]').forEach(roofline => {
    const rooflineElement = roofline as HTMLElement
    if (!rooflineElement.textContent || rooflineElement.textContent.trim() === '') {
      rooflineElement.style.visibility = 'hidden'
      rooflineElement.style.minHeight = '24px'
    }
  })
}

onBeforeMount(() => {
  // we need to inject a special headline tag, which is not a stack-standard element
  const specialHeadlineTag = '<div data-ems="ems-js-ada-native-overallheadline" class="text-heading-lg md:text-heading-2xl"><a href="#LINK-ME-BACK" class="underline-offset-2 text-secondary-500 decoration-from-font hover:text-primary-350"><h2>Special-Headline</h2></a></div>'
  document.querySelector('[data-ems="ems-native-adlabel"]')?.insertAdjacentHTML('afterend', specialHeadlineTag)
  window.autoNative = window.autoNative || {}
  window.autoNative.mapping = { ...articleMapping.value, ...sharedMapping.value }
})

watch(shouldLoadAda, (newVal) => {
  if (newVal) {
    injectAdaAutoNativeScript()
  }
})

// watch for changes in the DOM (by Ada) and handle roofline elements
const observeAdaInjection = () => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        handleRooflineElements()
      }
    })
  })

  observer.observe(document.body, { childList: true, subtree: true })
}

onMounted(() => {
  observeAdaInjection()
  if (shouldLoadAda.value) {
    injectAdaAutoNativeScript()
  }
})
</script>

<template>
  <div />
</template>
